import Typography from '@mui/material/Typography';
import React, { useMemo } from 'react';

import { parseUrl } from './parse-url';
import * as Styled from './url-parser.style';

const urlRegex =
  // eslint-disable-next-line max-len -- regex needs this length
  /\b[a-z][a-z0-9+.-]*:\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|]|\bwww\.[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|]|\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b|\b[A-Z0-9.-]+\.[A-Z]{2,}\b|\bmailto:[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|]/gi;

export type LinkBaseProps = {
  href?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  children: React.ReactNode;
  target?: string;
  rel?: string;
};

export type UrlParserProps = Omit<
  React.ComponentProps<typeof Typography>,
  'children'
> & {
  value: string;
  linkComponent: React.ComponentType<LinkBaseProps>;
  respectNewLines?: boolean;
  explicitTextCursor?: boolean;
  hasPaddingRight?: boolean;
};

export const UrlParser: React.FC<UrlParserProps> = ({
  value,
  linkComponent: Link,
  respectNewLines,
  explicitTextCursor,
  hasPaddingRight,
  ...rest
}) => {
  const elements = useMemo(() => {
    if (!value) {
      return [];
    }
    const matches = [...value.matchAll(urlRegex)];

    // If no URLs are found, just return the plain text.
    if (!matches.length) {
      return [value];
    }

    const _elements: (string | JSX.Element)[] = [];
    let lastIndex = 0;

    matches.forEach((match, index) => {
      // Add the text segment before the current URL match.
      if (match.index! > lastIndex) {
        _elements.push(value.substring(lastIndex, match.index));
      }

      const url = match[0];
      const href = parseUrl(url);

      // Add the clickable link element for the URL.
      _elements.push(
        <Link
          key={`url-${index}`}
          href={href}
          target="_blank" // Open link in a new tab
          rel="noopener noreferrer" // Security best practice
          // prevent click-events going up
          onClick={(e) => e.stopPropagation()}
        >
          {url}
        </Link>,
      );

      lastIndex = (match.index ?? 0) + url.length;
    });

    // add last part of value
    if (lastIndex < value.length) {
      _elements.push(value.substring(lastIndex));
    }

    return _elements;
  }, [value, Link]);

  return (
    <Styled.UrlParser
      $explicitTextCursor={!!explicitTextCursor}
      $hasPaddingRight={!!hasPaddingRight}
      $respectNewLines={!!respectNewLines}
      {...rest}
    >
      {elements.length ? elements : value}
    </Styled.UrlParser>
  );
};
